import { type DateRange } from "react-day-picker";

/** Convert local Date → "YYYY-MM-DD" (avoids UTC drift of `toISOString().slice(0,10)`). */
export function dateToLocalISO(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

export function parseISO(s: string | undefined): Date | undefined {
    if (!s) return undefined;
    const [y, m, d] = s.split("-").map(Number);
    if (y === undefined || m === undefined || d === undefined) return undefined;
    if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return undefined;
    return new Date(y, m - 1, d);
}

/** Guarantee from <= to: react-day-picker mode="range" doesn't sort. */
export function normalizeRange(range: DateRange | undefined): DateRange | undefined {
    if (!range?.from || !range.to) return range;
    if (range.to < range.from) return { from: range.to, to: range.from };
    return range;
}

interface DateRangePreset {
    value: string;
    label: string;
    hours?: number;
    days?: number;
}

// `label` is an English fallback; the displayed text is localised via
// `t("dateRange.preset.<value>")` (ui namespace) inside `DateRangeFilter`.
export const DATE_RANGE_PRESETS: DateRangePreset[] = [
    { value: "12h", label: "Last 12 hours", hours: 12 },
    { value: "24h", label: "Last 24 hours", hours: 24 },
    { value: "2d", label: "Last 2 days", days: 2 },
    { value: "7d", label: "Last 7 days", days: 7 },
    { value: "14d", label: "Last 14 days", days: 14 },
    { value: "30d", label: "Last 30 days", days: 30 },
    { value: "90d", label: "Last 90 days", days: 90 },
    { value: "180d", label: "Last 6 months", days: 180 },
    { value: "365d", label: "Last year", days: 365 },
];

export function dateRangeFromPreset(preset: string): { from: string; to: string } | null {
    const p = DATE_RANGE_PRESETS.find((d) => d.value === preset);
    if (!p) return null;
    const to = new Date();
    const from = new Date();
    if (p.hours) {
        from.setHours(from.getHours() - p.hours);
    } else if (p.days) {
        from.setDate(from.getDate() - p.days);
    }
    return { from: dateToLocalISO(from), to: dateToLocalISO(to) };
}

export function presetLabel(preset: string): string | null {
    return DATE_RANGE_PRESETS.find((d) => d.value === preset)?.label ?? null;
}

/** Parses stored daterange value: "preset:7d" | "custom:2026-01-01:2026-04-03" | undefined */
export function parseDateRangeValue(val: unknown): { from?: string; to?: string } {
    if (!val || typeof val !== "string") return {};
    if (val.startsWith("preset:")) {
        const preset = val.slice(7);
        return dateRangeFromPreset(preset) ?? {};
    }
    if (val.startsWith("custom:")) {
        const parts = val.slice(7).split(":");
        const [from, to] = parts;
        return { from: from?.length ? from : undefined, to: to?.length ? to : undefined };
    }
    return {};
}
